import { Box, Button, Stack, Typography } from '@mui/material';
import { Archive as ArchiveIcon } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useConfirm } from 'material-ui-confirm';

import { archiveResident } from '@/api/residents';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { SectionLabel } from '@/pages/v2/requests/request/components/request-details-card';

interface ArchiveUserCardProps {
  residentId: string;
  residentName: string;
}

export function ArchiveUserCard({
  residentId,
  residentName,
}: ArchiveUserCardProps): React.JSX.Element {
  const { community } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const archiveMutation = useMutation({
    mutationFn: () => archiveResident(community!.id, residentId),
    onSuccess: () => {
      toast.success('User archived successfully');
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      queryClient.invalidateQueries({ queryKey: ['resident', residentId] });
      navigate(paths.users.index);
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to archive user';
      toast.error(message);
    },
  });

  const handleArchive = async () => {
    try {
      await confirm({
        title: 'Archive User',
        description: `${residentName} will no longer appear in resident lists. Their data will be preserved and can be restored if needed.`,
        confirmationButtonProps: { color: 'warning' },
        confirmationText: 'Archive',
      });
      archiveMutation.mutate();
    } catch {
      // User cancelled
    }
  };

  if (!community) return <></>;

  return (
    <Box>
      <SectionLabel icon={<ArchiveIcon size={14} />}>Archive User</SectionLabel>
      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
        <Typography color="text.secondary" sx={{ fontSize: '0.8125rem' }} variant="body2">
          Archive this user to remove them from resident lists. Their data is preserved and
          they can be restored later if needed.
        </Typography>
        <Button
          color="warning"
          disabled={archiveMutation.isPending}
          onClick={handleArchive}
          size="small"
          sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          variant="outlined"
        >
          {archiveMutation.isPending ? 'Archiving…' : 'Archive User'}
        </Button>
      </Stack>
    </Box>
  );
}
